'use client';

import React, { createContext, useContext } from 'react';

import CssBaseline from '@mui/material/CssBaseline';
import { CssVarsProvider, useColorScheme } from '@mui/material/styles';

import { theme } from './muiTheme';

export type ThemeMode = 'light' | 'dark';
type ColorSchemeMode = 'light' | 'dark' | 'system';

interface ThemeContextType {
  mode: ThemeMode;
  colorScheme: ColorSchemeMode;
  setColorScheme: (mode: ColorSchemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

// Inner component that has access to MUI's color scheme hook
const ThemeContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { mode, setMode, systemMode } = useColorScheme();

  // Get the actual resolved mode: if user selected 'system', use the system's actual mode
  const resolvedMode = mode === 'system' ? systemMode : mode;
  const themeMode: ThemeMode = resolvedMode === 'dark' ? 'dark' : 'light';
  const colorScheme: ColorSchemeMode = mode || 'system';

  const setColorScheme = (newMode: ColorSchemeMode) => {
    setMode(newMode);
  };

  return (
    <ThemeContext.Provider value={{ mode: themeMode, colorScheme, setColorScheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  return (
    <CssVarsProvider theme={theme} defaultMode="system">
      <CssBaseline />
      <ThemeContextProvider>{children}</ThemeContextProvider>
    </CssVarsProvider>
  );
};
